// src/admin/components/users/UserForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiUserAddLine,
  RiSaveLine,
  RiCloseLine,
  RiArrowLeftLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiShieldUserLine,
  RiMailLine,
  RiUserLine,
  RiLockPasswordLine,
  RiPhoneLine,
  RiUserStarLine
} from 'react-icons/ri';
import {
  createUser,
  getUser,
  updateUser,
  validateUserData,
  USER_ROLES
} from '../../../models/userModel';
import { useTheme } from '../../../contexts/ThemeContext';

const UserForm = ({ isEdit = false }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'front_desk'
  });

  // Translation for validation messages
  const validationMessages = {
    first_name_required: t('first_name_required') || 'First name is required',
    last_name_required: t('last_name_required') || 'Last name is required',
    email_required: t('email_required') || 'Email is required',
    invalid_email: t('invalid_email') || 'Please enter a valid email address',
    username_required: t('username_required') || 'Username is required',
    username_too_short: t('username_too_short') || 'Username must be at least 3 characters',
    password_required: t('password_required') || 'Password is required',
    password_too_short: t('password_too_short') || 'Password must be at least 6 characters',
    passwords_do_not_match: t('passwords_do_not_match') || 'Passwords do not match',
    phone_invalid_format: t('phone_invalid_format') || 'Please enter a valid phone number'
  };

  // Fetch user data if in edit mode
  useEffect(() => {
    const fetchUserData = async () => {
      if (isEdit && id) {
        try {
          setLoading(true);
          const response = await getUser(id);
          const { user } = response;

          setFormData({
            firstName: user.firstName || user.first_name,
            lastName: user.lastName || user.last_name,
            email: user.email,
            username: user.username,
            password: '',
            confirmPassword: '',
            phone: user.phone || '',
            role: user.role
          });

          setError(null);
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError(err.message || 'Failed to load user data');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [isEdit, id]);

  // Validate a single field
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'firstName':
        if (!value.trim()) {
          error = validationMessages.first_name_required;
        }
        break;

      case 'lastName':
        if (!value.trim()) {
          error = validationMessages.last_name_required;
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = validationMessages.email_required;
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          error = validationMessages.invalid_email;
        }
        break;

      case 'username':
        if (!value.trim()) {
          error = validationMessages.username_required;
        } else if (value.length < 3) {
          error = validationMessages.username_too_short;
        }
        break;

      case 'password':
        if (!isEdit && !value) {
          error = validationMessages.password_required;
        } else if (value && value.length < 6) {
          error = validationMessages.password_too_short;
        }
        break;

      case 'confirmPassword':
        if (value !== formData.password) {
          error = validationMessages.passwords_do_not_match;
        }
        break;

      case 'phone':
        if (value && !/^\+?[\d\s\-()]{10,}$/.test(value)) {
          error = validationMessages.phone_invalid_format;
        }
        break;

      default:
        break;
    }

    return error;
  };

  // Handle field blur for live validation
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({ ...touched, [name]: true });

    const error = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Handle form input changes with live validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // If the field has been touched, validate it
    if (touched[name]) {
      const error = validateField(name, value);
      setFormErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};
    const newTouched = {};

    // Mark all fields as touched
    Object.keys(formData).forEach(key => {
      newTouched[key] = true;
      newErrors[key] = validateField(key, formData[key]);
    });

    setTouched(newTouched);
    setFormErrors(newErrors);

    // Return true if no errors
    return !Object.values(newErrors).some(error => error);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = document.querySelector('.error-field');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      setSaving(true);

      // Format the data according to the updated backend model
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        username: formData.username,
        phone: formData.phone || null,
        role: formData.role
      };

      if (!isEdit) {
        userData.password = formData.password;
        await createUser(userData);
      } else {
        // Only include password if it's provided
        if (formData.password) {
          userData.password = formData.password;
        }

        await updateUser(id, userData);
      }

      navigate('/admin/users');
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} user`);
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  // Render input field with validation
  const renderField = (name, label, type = 'text', icon, placeholder, disabled = false, required = true) => {
    const hasError = touched[name] && formErrors[name];
    const isValid = touched[name] && !formErrors[name];

    return (
      <div className={hasError ? 'error-field' : ''}>
        <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
          {label} {required ? '*' : ''}
        </label>
        <div className="relative">
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
            }`}>
            {icon}
          </div>

          <input
            type={type}
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            className={`w-full ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} p-2.5 text-sm rounded-lg transition-all duration-200 ${isDarkMode
              ? 'bg-zinc-800 border-gray-600 text-white focus:ring-red-500 focus:border-red-500'
              : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-red-500 focus:border-red-500'
              } border ${hasError
                ? 'border-red-500 shadow-sm shadow-red-500/30'
                : isValid
                  ? 'border-green-500 shadow-sm shadow-green-500/30'
                  : ''
              } ${disabled ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            placeholder={placeholder}
          />

          {hasError && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center text-red-500`}>
              <RiErrorWarningLine size={18} />
            </div>
          )}

          {isValid && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center text-green-500`}>
              <RiCheckLine size={18} />
            </div>
          )}
        </div>

        {hasError && (
          <p className="mt-1 text-sm text-red-500 animate-fadeIn">
            {formErrors[name]}
          </p>
        )}
      </div>
    );
  };

  // Render select field with validation
  const renderSelect = (name, label, options, icon, required = true) => {
    const hasError = touched[name] && formErrors[name];
    const isValid = touched[name] && !formErrors[name];

    return (
      <div className={hasError ? 'error-field' : ''}>
        <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
          {label} {required ? '*' : ''}
        </label>
        <div className="relative">
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'
            }`}>
            {icon}
          </div>

          <select
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} p-2.5 text-sm rounded-lg appearance-none bg-no-repeat transition-all duration-200 ${isDarkMode
              ? 'bg-zinc-800 border-gray-600 text-white focus:ring-red-500 focus:border-red-500'
              : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-red-500 focus:border-red-500'
              } border ${hasError
                ? 'border-red-500 shadow-sm shadow-red-500/30'
                : isValid
                  ? 'border-green-500 shadow-sm shadow-green-500/30'
                  : ''
              }`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: isRTL ? 'left 0.5rem center' : 'right 0.5rem center',
              backgroundSize: '1.5em 1.5em'
            }}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {hasError && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-8 pl-1' : 'right-8 pr-1'} flex items-center text-red-500`}>
              <RiErrorWarningLine size={18} />
            </div>
          )}

          {isValid && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-8 pl-1' : 'right-8 pr-1'} flex items-center text-green-500`}>
              <RiCheckLine size={18} />
            </div>
          )}
        </div>

        {hasError && (
          <p className="mt-1 text-sm text-red-500 animate-fadeIn">
            {formErrors[name]}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg p-8 text-center`}>
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className={`inline-block h-12 w-12 animate-spin rounded-full border-4 ${isDarkMode ? 'border-red-500 border-t-transparent' : 'border-red-500 border-t-transparent'
            }`}></div>
          <p className={`mt-2 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('loading_user_data')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg overflow-hidden transition-all duration-300`}>
      {/* Header */}
      <div className={`p-5 ${isDarkMode ? 'bg-gray-750 border-b border-gray-700' : 'bg-zinc-50 border-b border-gray-200'}`}>
        <div className="flex justify-between items-center">
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} flex items-center`}>
            <RiUserAddLine className={`me-3 ${isDarkMode ? 'text-red-400' : 'text-red-500'} text-2xl`} />
            <span>
              {isEdit ? t('edit_user') : t('add_new_user')}
            </span>
          </h2>

          <button
            onClick={() => navigate('/admin/users')}
            className={`p-2 rounded-full ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:text-gray-800 hover:bg-gray-200'
              } transition-all duration-200`}
            aria-label="Back to users list"
          >
            {isRTL ? <RiArrowLeftLine className="rotate-180" size={22} /> : <RiArrowLeftLine size={22} />}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className={`mx-5 mt-5 p-4 rounded-lg ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-50 text-red-700 border border-red-200'
          } flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} animate-fadeIn`}>
          <RiErrorWarningLine className="flex-shrink-0 text-xl" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information Section */}
          <div className={`col-span-full p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-4`}>
            <h3 className={`text-md font-medium mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center`}>
              <RiUserLine className="me-2" /> {t('personal_information')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* First Name */}
              {renderField(
                'firstName',
                t('first_name'),
                'text',
                <RiUserLine size={18} />,
                t('enter_first_name')
              )}

              {/* Last Name */}
              {renderField(
                'lastName',
                t('last_name'),
                'text',
                <RiUserLine size={18} />,
                t('enter_last_name')
              )}

              {/* Email */}
              {renderField(
                'email',
                t('email'),
                'email',
                <RiMailLine size={18} />,
                t('enter_email')
              )}

              {/* Phone Number */}
              {renderField(
                'phone',
                t('phone_number'),
                'text',
                <RiPhoneLine size={18} />,
                t('enter_phone_number'),
                false,
                false
              )}
            </div>
          </div>

          {/* Account Information Section */}
          <div className={`col-span-full p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-4`}>
            <h3 className={`text-md font-medium mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center`}>
              <RiShieldUserLine className="me-2" /> {t('account_information')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Username */}
              {renderField(
                'username',
                t('username'),
                'text',
                <RiUserStarLine size={18} />,
                t('enter_username'),
                isEdit
              )}

              {/* Role */}
              {renderSelect(
                'role',
                t('role'),
                USER_ROLES,
                <RiShieldUserLine size={18} />
              )}
            </div>
          </div>

          {/* Security Section */}
          <div className={`col-span-full p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-4`}>
            <h3 className={`text-md font-medium mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center`}>
              <RiLockPasswordLine className="me-2" /> {t('security')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Password */}
              {renderField(
                'password',
                t('password'),
                'password',
                <RiLockPasswordLine size={18} />,
                isEdit ? t('leave_blank_to_keep_current') : t('enter_password'),
                false,
                !isEdit
              )}

              {/* Confirm Password */}
              {renderField(
                'confirmPassword',
                t('confirm_password'),
                'password',
                <RiLockPasswordLine size={18} />,
                t('confirm_password'),
                false,
                !isEdit
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex justify-end space-s-4">
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isDarkMode
              ? 'bg-zinc-800 text-white hover:bg-gray-600'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              } flex items-center`}
            disabled={saving}
          >
            <RiCloseLine className="me-1.5" size={18} />
            {t('cancel')}
          </button>

          <button
            type="submit"
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isDarkMode
              ? 'bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-700/20'
              : 'bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-500/20'
              } flex items-center`}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className={`h-4 w-4 ${isRTL ? 'ms-2' : 'mr-2'} animate-spin rounded-full border-2 border-white border-t-transparent`}></div>
                {t('saving')}
              </>
            ) : (
              <>
                <RiSaveLine className="me-1.5" size={18} />
                {isEdit ? t('update') : t('save')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Add custom CSS for animations */}
      <style jsx={true}>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default UserForm;